import React from 'react';
import { AssessmentPoint } from '../../types';
import { DEGREE_LEVELS } from './evaluationTypes';

interface GroupedCriteria {
  category: string;
  points: AssessmentPoint[];
}

interface EvaluationFormProps {
  candidateName: string;
  setCandidateName: (value: string) => void;
  jobTitle: string;
  setJobTitle: (value: string) => void;
  evaluatorName: string;
  setEvaluatorName: (value: string) => void;
  ratings: { [key: string]: number };
  onRatingChange: (pointId: string, score: number) => void;
  notes: string;
  setNotes: (value: string) => void;
  groupedCriteria: GroupedCriteria[];
  onSubmit: () => void;
  onCancel: () => void;
}

const EvaluationForm: React.FC<EvaluationFormProps> = ({
  candidateName, setCandidateName,
  jobTitle, setJobTitle,
  evaluatorName, setEvaluatorName,
  ratings, onRatingChange,
  notes, setNotes,
  groupedCriteria,
  onSubmit, onCancel,
}) => {
  return (
    <div className="card">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">New Candidate Evaluation</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Name *</label>
            <input
              type="text"
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter candidate name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter job title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Evaluator Name *</label>
            <input
              type="text"
              value={evaluatorName}
              onChange={(e) => setEvaluatorName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter evaluator name"
            />
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Rating Scale</h4>
          <div className="flex flex-wrap gap-4">
            {DEGREE_LEVELS.map((level) => (
              <div key={level.value} className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-medium ${level.color}`}>
                {level.label} ({level.range})
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Assessment Criteria</h3>
          <div className="space-y-6">
            {groupedCriteria.map((group) => (
              <div key={group.category} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
                  <h4 className="text-sm font-semibold text-blue-900">{group.category}</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {group.points.map((point) => (
                    <div key={point.id} className="px-4 py-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-medium text-gray-900">{point.name}</h5>
                          {point.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{point.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-3 flex-shrink-0">
                          {DEGREE_LEVELS.map((level) => (
                            <label key={level.value} className="flex items-center space-x-1.5 cursor-pointer group">
                              <input
                                type="radio"
                                name={`rating-${point.id}`}
                                value={level.value}
                                checked={ratings[point.id] === level.value}
                                onChange={() => onRatingChange(point.id, level.value)}
                                className={`h-4 w-4 ${level.radioColor} focus:ring-2 focus:ring-offset-1 border-gray-300`}
                              />
                              <span className={`text-xs font-medium ${
                                ratings[point.id] === level.value ? 'text-gray-900' : 'text-gray-500 group-hover:text-gray-700'
                              }`}>
                                {level.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Additional notes about the candidate..."
          />
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!candidateName || !jobTitle || !evaluatorName}
          >
            Save Evaluation
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationForm;
